import { useCallback, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Layout,
  theme,
  Table,
  Tag,
  Tabs,
  Typography,
  message,
  Alert,
} from "antd";
import { ToolOutlined } from "@ant-design/icons";
import type { ColumnsType } from "antd/es/table";
import LoadingOverlay from "react-loading-overlay-ts";
import dayjs from "dayjs";

import TopBar from "../../components/TopBar";
import AdminTopBar from "../../components/TopBar/AdminTopbar";
import SideBar from "../../components/SideBar";
import AdminSideBar from "../../components/SideBar/AdminSidebar";
import { AuthContext } from "../../context/AuthContext";
import {
  getLapseRunsApi,
  LapseRun,
  LapseRunStatus,
} from "../../services/Admin/LapseRunApis";

const { Content } = Layout;

const RUN_STATUS_TABS: { key: LapseRunStatus; label: string }[] = [
  { key: "PENDING", label: "Pending" },
  { key: "APPROVED", label: "Approved" },
  { key: "COMPLETED", label: "Completed" },
  { key: "REJECTED", label: "Rejected" },
  { key: "FAILED", label: "Failed" },
];

const RUN_STATUS_COLOR: Record<LapseRunStatus, string> = {
  PENDING: "orange",
  APPROVED: "blue",
  COMPLETED: "green",
  REJECTED: "default",
  FAILED: "red",
};

const includedFromRun = (run: LapseRun) =>
  run.includedCount ??
  Math.max((run.proposedCount ?? 0) - (run.excludedCount ?? 0), 0);

const MaintenancePage: React.FC<{ adminLayout?: boolean }> = ({
  adminLayout = false,
}) => {
  const { currentUser } = useContext(AuthContext);
  const navigate = useNavigate();
  const {
    token: { colorBgContainer },
  } = theme.useToken();

  const [isActive, setActive] = useState(true);
  const [runsByStatus, setRunsByStatus] = useState<
    Partial<Record<LapseRunStatus, LapseRun[]>>
  >({});
  const [status, setStatus] = useState<LapseRunStatus>("PENDING");

  const role = currentUser?.Role?.Name;
  const isSuperAdmin = role === "Super Admin";
  const listBase = adminLayout ? "/admin/maintenance" : "/maintenance";
  const runs = runsByStatus[status] ?? [];
  const pendingCount = runsByStatus.PENDING?.length ?? 0;

  const loadAllRuns = useCallback(() => {
    setActive(true);
    Promise.all(
      RUN_STATUS_TABS.map((tab) =>
        getLapseRunsApi(tab.key)
          .then((res) => {
            if (!res || res.success === false) {
              return { status: tab.key, runs: [] as LapseRun[], error: true };
            }
            return {
              status: tab.key,
              runs: Array.isArray(res.data) ? (res.data as LapseRun[]) : [],
              error: false,
            };
          })
          .catch(() => ({
            status: tab.key,
            runs: [] as LapseRun[],
            error: true,
          }))
      )
    )
      .then((results) => {
        const next: Partial<Record<LapseRunStatus, LapseRun[]>> = {};
        let anyError = false;
        for (const result of results) {
          next[result.status] = result.runs;
          if (result.error) anyError = true;
        }
        setRunsByStatus(next);
        if (anyError) {
          message.error("Unable to load some lapse runs.");
        }
      })
      .finally(() => setActive(false));
  }, []);

  useEffect(() => {
    if (role && !isSuperAdmin) {
      navigate(adminLayout ? "/admin/dashboard" : "/dashboard");
    }
  }, [role, isSuperAdmin, navigate, adminLayout]);

  useEffect(() => {
    if (!isSuperAdmin) return;
    loadAllRuns();
  }, [isSuperAdmin, loadAllRuns]);

  const columns: ColumnsType<LapseRun> = [
    {
      title: "ID",
      dataIndex: "id",
      width: 80,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (value: LapseRunStatus) => (
        <Tag color={RUN_STATUS_COLOR[value] || "default"}>{value}</Tag>
      ),
    },
    {
      title: "Owning group",
      render: (_: unknown, row: LapseRun) =>
        row.OwningGroup?.GroupName || "—",
    },
    {
      title: "Created",
      dataIndex: "createdAt",
      render: (value?: string | null) =>
        value ? dayjs(value).format("YYYY-MM-DD HH:mm") : "-",
    },
    {
      title: "Proposed",
      dataIndex: "proposedCount",
    },
    {
      title: "Included",
      render: (_: unknown, row: LapseRun) => includedFromRun(row),
    },
    {
      title: "Excluded",
      dataIndex: "excludedCount",
    },
    {
      title: "Lapsed",
      dataIndex: "lapsedCount",
    },
    {
      title: "Errors",
      dataIndex: "errorCount",
    },
  ];

  return (
    <LoadingOverlay active={isActive} spinner>
      <Layout style={{ minHeight: "100vh" }}>
        {adminLayout ? <AdminSideBar /> : <SideBar />}
        <Layout className="site-layout">
          {adminLayout ? <AdminTopBar /> : <TopBar />}
          <Content>
            <div
              style={{
                padding: 24,
                minHeight: "100%",
                background: colorBgContainer,
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 16,
                  flexWrap: "wrap",
                  marginBottom: 16,
                }}
              >
                <h2 style={{ margin: 0 }}>
                  <ToolOutlined
                    style={{ paddingRight: "5px", color: "#f04f05" }}
                  />
                  Maintenance
                </h2>
              </div>
              <Typography.Text type="secondary">
                Review pending lapse runs before policies are lapsed and SMS is
                sent.
              </Typography.Text>
              {pendingCount > 0 && status !== "PENDING" && (
                <Alert
                  style={{ marginTop: 16 }}
                  type="warning"
                  showIcon
                  message={`${pendingCount} lapse run${
                    pendingCount === 1 ? "" : "s"
                  } awaiting approval`}
                  description={
                    <Typography.Link onClick={() => setStatus("PENDING")}>
                      View pending runs
                    </Typography.Link>
                  }
                />
              )}

              <Tabs
                type="card"
                style={{ marginTop: 16 }}
                activeKey={status}
                onChange={(key) => setStatus(key as LapseRunStatus)}
                items={RUN_STATUS_TABS.map((tab) => ({
                  key: tab.key,
                  label:
                    tab.key === "PENDING" && pendingCount > 0
                      ? `${tab.label} (${pendingCount})`
                      : tab.label,
                }))}
              />

              <Table
                rowKey="id"
                columns={columns}
                dataSource={runs}
                pagination={{ pageSize: 20 }}
                onRow={(record) => ({
                  onClick: () => navigate(`${listBase}/${record.id}`),
                  style: { cursor: "pointer" },
                })}
              />
            </div>
          </Content>
        </Layout>
      </Layout>
    </LoadingOverlay>
  );
};

export default MaintenancePage;
